<!--

	A list that is rendered as a bootstrap table.

-->
<template>
	<conditional :condition="!isEmpty" :message-otherwise="messageIfEmpty">
		<div class="mia-BootstrapTableList" :class="{'table-responsive': options.backendOptions.responsive !== false}">
			<table
				:class="{
					'table': true,
					'table-hover': options.backendOptions.hover !== false,
					'table-condensed': options.backendOptions.condensed !== false,
					'table-striped': options.backendOptions.striped}"
			>
				<thead>
					<tr>
						<th v-for="(columnKey, index) in backendOptions.columnKeys" :key="index" v-tr="columnKey" />
					</tr>
				</thead>
				<tbody>
					<list-item-loader v-for="(item, index) in visibleItems" :key="index + offset" :from="item" :item-props="itemProps" :conversions="conversions" :component="options.component" :index="index" />
				</tbody>
			</table>
			<nav v-if="hasPreviousPage || hasNextPage" class="center-text">
				<ul class="pagination">
					<li :class="{disabled: !hasPreviousPage}">
						<a href="#" class="paginationButton" @click.prevent="gotoPage(page - 1)">
							&laquo;
						</a>
					</li>
					<li v-for="p in paginationPages" :key="p" :class="{active: page === p}">
						<a href="#" class="paginationButton" @click.prevent="gotoPage(p)">
							{{ p + 1 }}
						</a>
					</li>
					<li :class="{disabled: !hasNextPage}">
						<a href="#" class="paginationButton" @click.prevent="gotoPage(page + 1)">
							&raquo;
						</a>
					</li>
				</ul>
			</nav>
		</div>
		<template #replacement>
			<slot name="empty" />
		</template>
	</conditional>
</template>
<script lang="ts">
import { Component, Watch, toNative } from "vue-facing-decorator";
import Conditional from "../../ConditionalReplacement.vue";
import ListItemLoader from "./ListItemLoader.vue";
import { VueListBase } from "./VueListBase";
import { type IBootstrapTableOptions } from "./VueListOptions";

@Component({
	components: { Conditional, ListItemLoader },
})
export class BootstrapTableList<S extends string | number, T> extends VueListBase<S, T, IBootstrapTableOptions> {
	/**
	 * The offset of visibleItems and items, i.e., visibleItems[i] == items[i + offset].
	 */
	offset = 0;

	/**
	 * Jump to an offset (zero-based), i.e., make sure that this offset is visible on the current page.
	 */
	public goto(offset: number): void {
		this.gotoPage(Math.floor(offset / this.pageSize));
		void this.preload(this.offset);
	}

	@Watch("isResetting", { immediate: true })
	protected onReset(isResetting: number): void {
		if (!isResetting) {
			this.goto(0);
		}
	}

	get visibleItems(): S[] {
		if (this.offset >= this.totalCount) {
			// If for some reason, the offset became invalid, e.g., because the data in the backend changed,
			// we go back to a reasonable offset.
			this.goto(this.pages - 1);
		}
		return this.items.slice(this.offset, this.offset + this.options.pageSize);
	}

	/**
	 * The current page, zero-based.
	 */
	get page(): number {
		return Math.floor(this.offset / this.options.pageSize);
	}

	/**
	 * The indices of the pages that are accessible through pagination, zero-based.
	 */
	get paginationPages(): number[] {
		const previousPages = 3;
		const followingPages = 3;
		const totalPages = previousPages + followingPages + 1;

		// We'll offer the range [a, b) to the user
		// The idea is to put the current page in the center of the pagination, that's what Google Search
		// does, so apparently that's a good idea.
		let a = this.page - previousPages;
		let b = a + totalPages;
		if (a < 0) {
			a = 0;
			b = a + totalPages;
		}
		if (b >= this.pages) {
			b = this.pages;
			a = Math.max(b - totalPages, 0);
		}

		return Array.from({ length: b - a }, (_, i) => i + a);
	}

	get hasPreviousPage(): boolean {
		return this.page !== 0;
	}

	get hasNextPage(): boolean {
		return this.totalCount > this.visibleItems.length + this.offset;
	}

	get pages(): number {
		return Math.ceil(this.totalCount / this.options.pageSize);
	}

	/**
	 * Jump to a page (zero-based.)
	 */
	gotoPage(page: number): void {
		this.offset = page * this.options.pageSize;
		if (this.offset < 0) {
			this.offset = 0;
		}
		if (this.offset >= this.totalCount) {
			this.offset = (this.pages - 1) * this.options.pageSize;
		}
		void this.preload(this.offset);
	}
}

export default toNative(BootstrapTableList);
</script>
<style lang="scss">
.mia-BootstrapTableList {
	> table + nav {
		// When we show a pagination we shift the table margin-bottom to underneath the navigation
		margin-top: -#{theme.$line-height-computed};
		margin-bottom: theme.$line-height-computed;
	}

	> nav {
		display: flex;
		justify-content: center;
	}
}
</style>
